/**
 * Spotlight overlay component that highlights a target element
 */

import { useEffect, useState, useCallback } from "react";
import { cn } from "@/lib/utils";

type SpotlightProps = {
  targetSelector: string;
  padding?: number;
  isVisible: boolean;
  onTargetClick?: () => void;
  isCompleteStep?: boolean;
};

type TargetRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export const Spotlight = ({
  targetSelector,
  padding = 8,
  isVisible,
  onTargetClick,
  isCompleteStep = false,
}: SpotlightProps) => {
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);

  const updateTargetRect = useCallback(() => {
    if (!targetSelector) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(targetSelector);
    if (!element) {
      setTargetRect(null);
      return;
    }

    const rect = element.getBoundingClientRect();
    setTargetRect({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });
  }, [targetSelector, padding]);

  useEffect(() => {
    if (!isVisible) return;

    updateTargetRect();

    // Update on scroll and resize
    const handleUpdate = () => {
      requestAnimationFrame(updateTargetRect);
    };

    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);

    // Use ResizeObserver for element size changes (only if we have a valid selector)
    let resizeObserver: ResizeObserver | null = null;
    if (targetSelector) {
      resizeObserver = new ResizeObserver(handleUpdate);
      const element = document.querySelector(targetSelector);
      if (element) {
        resizeObserver.observe(element);
      }
    }

    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isVisible, targetSelector, updateTargetRect]);

  if (!isVisible) return null;

  // If no target or completion step, show just the dark overlay (no spotlight cutout)
  if (!targetRect || !targetSelector || isCompleteStep) {
    return (
      <div
        className={cn(
          "fixed inset-0 z-[9998] transition-opacity duration-300",
          "bg-black/80 backdrop-blur-sm"
        )}
        onClick={onTargetClick}
      />
    );
  }

  // Create SVG mask for the spotlight effect
  return (
    <>
      {/* Dark overlay with cutout */}
      <svg
        className="fixed inset-0 z-[9998] pointer-events-none"
        style={{ width: "100vw", height: "100vh" }}
      >
        <defs>
          <mask id="spotlight-mask">
            {/* White = visible, Black = hidden */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left}
              y={targetRect.top}
              width={targetRect.width}
              height={targetRect.height}
              rx="8"
              fill="black"
              className="transition-all duration-300 ease-out"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.8)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Clickable overlay areas (not the spotlight) */}
      <div
        className="fixed inset-0 z-[9998]"
        style={{
          clipPath: `polygon(
            0 0, 100% 0, 100% 100%, 0 100%, 0 0,
            ${targetRect.left}px ${targetRect.top}px,
            ${targetRect.left}px ${targetRect.top + targetRect.height}px,
            ${targetRect.left + targetRect.width}px ${targetRect.top + targetRect.height}px,
            ${targetRect.left + targetRect.width}px ${targetRect.top}px,
            ${targetRect.left}px ${targetRect.top}px
          )`,
        }}
        onClick={onTargetClick}
      />

      {/* Highlight border around target */}
      <div
        className="fixed z-[9999] pointer-events-none rounded-lg ring-2 ring-emerald-500 ring-offset-2 ring-offset-transparent transition-all duration-300 ease-out"
        style={{
          top: targetRect.top,
          left: targetRect.left,
          width: targetRect.width,
          height: targetRect.height,
        }}
      >
        {/* Pulse animation */}
        <div className="absolute inset-0 rounded-lg animate-pulse ring-2 ring-emerald-500/50" />
      </div>
    </>
  );
};
